import type { ChunkRef } from "./map-overview.ts";

/**
 * Small pure rules of the overview's picture while it is edited (ADR-109's amendment of 2026-10-13, decisions 48
 * and 48's last point): which waiting chunks a frame draws, when a prop is too small to scale, and the one colour
 * such a prop is drawn in.
 */

/**
 * The lowest alpha, of 255, at which a pixel counts as opaque for a frame's mean colour.
 */
const OPAQUE_ALPHA = 128;

/**
 * The chunks a frame draws out of those waiting: the nearest to a chunk first (the one under the pointer's last
 * place), the queue's order breaking a tie so the oldest goes first, and at most `share` of them. A stroke that
 * touches `n` chunks is therefore on the picture within `ceil(n / share)` frames whatever the pointer does.
 *
 * @param waiting The chunks waiting, in the order they were queued.
 * @param near The chunk to draw nearest first, or null for the queue's order.
 * @param share The most chunks a frame draws.
 * @returns The chunks to draw, a new array.
 * @throws RangeError when the share is not a positive whole number.
 */
export function chunksToDraw(waiting: readonly ChunkRef[], near: ChunkRef | null, share: number): ChunkRef[] {
  if (!Number.isInteger(share) || share < 1) {
    throw new RangeError(`share must be a positive whole number, got ${share}`);
  }
  if (near === null) {
    return waiting.slice(0, share);
  }
  const ranked = waiting.map((chunk, order) => ({ chunk, order, distance: Math.max(Math.abs(chunk.cx - near.cx), Math.abs(chunk.cy - near.cy)) }));
  ranked.sort((a, b) => a.distance - b.distance || a.order - b.order);
  return ranked.slice(0, share).map((entry) => entry.chunk);
}

/**
 * Whether a frame scaled by a factor is under one pixel in either direction, so that scaling it would leave a faint
 * smear and the painter draws one pixel of its mean colour instead.
 *
 * @param width The frame's width in its own pixels.
 * @param height The frame's height in its own pixels.
 * @param factor Picture pixels to a frame pixel.
 * @returns True when either scaled side is under 1.
 */
export function isSubPixel(width: number, height: number, factor: number): boolean {
  return width * factor < 1 || height * factor < 1;
}

/**
 * The mean colour of a frame's opaque pixels.
 *
 * @param pixels The frame's pixels as red, green, blue, alpha in turn, each 0 to 255.
 * @returns The mean colour, each channel rounded, or null when no pixel is opaque.
 * @throws RangeError when the length is not a whole number of pixels.
 */
export function meanOpaqueColour(pixels: ArrayLike<number>): { readonly r: number; readonly g: number; readonly b: number } | null {
  if (pixels.length % 4 !== 0) {
    throw new RangeError(`pixels must be whole red, green, blue, alpha groups, got ${pixels.length} values`);
  }
  let r = 0;
  let g = 0;
  let b = 0;
  let count = 0;
  for (let i = 0; i < pixels.length; i += 4) {
    if ((pixels[i + 3] as number) >= OPAQUE_ALPHA) {
      r += pixels[i] as number;
      g += pixels[i + 1] as number;
      b += pixels[i + 2] as number;
      count += 1;
    }
  }
  return count === 0 ? null : { r: Math.round(r / count), g: Math.round(g / count), b: Math.round(b / count) };
}
